<template>
  <div class="p-question-select" :class="{'p-question-select--vertical': isVertical}">
    <div class="c-form-select" @click="openPopup(answer)" :ref="(el) => {refSelectBox[answerFormatId] = el}">
      <div class="output" :tabindex="tabIndex" @keyup.enter="openPopup(answer)" :data-prev-is-correctAnswer="prevIsCorrectAnswer">
        <div class="label"><markdown-view :rawText="selectedAnswer" :souruby="isSouruby"></markdown-view></div>
        <div class="arrow"></div>
      </div>
    </div>
    <div :ref="(el) => {refPopup[answerFormatId] = el}" class="p-question-select__item"
         :class="{'is-open': isAnswerPopup, 'topPart': isTopPart, 'bottomPart': isBottomPart, 'leftMostPart': isLeftMostPart}">
      <div class="close-layer" @click="closePopup"></div>
      <answer-radio-button v-if="selectListRadio"
                           :answer="answer"
                           :from-select="'selectRadio_'+ answerFormatId"
                           :is-vertical="isVertical"
                           :is-souruby="isSouruby"
                           :tab-index="isAnswerPopup ? 0 : -1"
                           v-model="radioValue"
                           :prev-answer-text="prevAnswerText"
                           @input="selectLabelRadio($event)"
                           @select-label="selectAnswerRadio($event)"
      ></answer-radio-button>
      <answer-checkbox v-else
                       :answer="answer"
                       :from-select="'selectCheckbox' + answerFormatId"
                       :is-vertical="isVertical"
                       :is-souruby="isSouruby"
                       :tab-index="isAnswerPopup ? 0 : -1"
                       v-model="checkboxValueList"
                       :prev-answer-text="prevAnswerText"
                       @input="selectLabelCheckbox($event)"
                       @select-label="selectAnswerCheckbox($event)"
      ></answer-checkbox>
      <div class="p-question-select__item__btn" v-if="!selectListRadio">
        <a :class="{'c-button-blue':true, '-vertical-no-icon':isVertical, '-no-icon':!isVertical}" @click="selectValue" @keyup.enter="selectValue" :tabindex="isAnswerPopup ? 0 : -1"><span class="label">決定</span></a>
      </div>
      <span class="p-question-select__item__close" @click="closePopup"><span class="inner"></span></span>
      <!--For make focus cycle-->
      <span :tabindex="isAnswerPopup ? 0 : -1" @focus="focusFirstElement()"></span>
    </div>
  </div>
</template>

<script setup lang="ts">
  import {ref, nextTick, onMounted} from "vue";
  import {LAYOUT, QUIZTYPE} from "@/consts/answer_column";
  import {CHOICESLABEL} from "@/consts/choicesLabel";

  const isAnswerPopup = ref(false)
  const checkboxValueList = ref([])
  const radioValue = ref("")
  const selectedAnswer = ref("")
  const popupFixedSize = ref(350)
  const isTopPart = ref(false)
  const isBottomPart = ref(false)
  const isLeftMostPart = ref(false)
  // save previous focus element for auto focus next element in radio selection
  const previousFocusElement = ref<any>(null)
  const refPopup = ref([])
  const refSelectBox = ref([])
  
  const props = withDefaults(
    defineProps<{
      answer: any;
      isVertical?: boolean;
      prevAnswerText?: string;
      answerFormatId?: string;
      tabIndex?: number;
      isSouruby?: boolean;
      isSorting?: boolean;
      selectListRadio?: boolean;
      prevIsCorrectAnswer?: boolean;
    }>(),
    {
      prevAnswerText: "",
      answerFormatId: "",
      isVertical: false,
      isSouruby: false,
      isSorting: false,
      selectListRadio: false,
      tabIndex: 0,
      prevIsCorrectAnswer: false,
    }
  );
  
  const emit = defineEmits<{
    (e: 'select-value', id: any): void,
    (e: 'set-tab-index', id: number): void,
    (e: 'focus-next-item', id: any): void,
  }>()

  onMounted(() => {
    // restore answer selection for skip question
    if(props.prevAnswerText){
      // action for radio button
      if(props.selectListRadio){
        radioValue.value = props.prevAnswerText
        selectedAnswer.value = props.prevAnswerText
        // if answer display option is not original text like "ア","イ","ウ"... | "Ⓐ","Ⓑ","Ⓒ"... | "1","2","3",...
        // and answer format is sorting then fetch the choices labes value
        if(props.answer.display_options.choicesLabel !== 1 && props.isSorting){
          selectedAnswer.value = CHOICESLABEL[props.answer.display_options.choicesLabel][props.answer.body.answers.map(i => i.answerText).indexOf(props.prevAnswerText)]
        }
        // answer display option is not original text like "ア","イ","ウ"... | "Ⓐ","Ⓑ","Ⓒ"... | "1","2","3",... but not sorting formal
        else if(props.answer.display_options.choicesLabel !== 1){
          let originalAnswerText = props.answer.body.answers.map(i => i.answerText)
          // find original restore answer text from cholces label
          radioValue.value = originalAnswerText[CHOICESLABEL[props.answer.display_options.choicesLabel].indexOf(props.prevAnswerText)]
        }
        // emit the restore text to save in store
        emit('select-value',{format_id:props.answerFormatId, value:radioValue.value})
      }
      // action for multiple selected options
      else{
        // make a all selected restore answer list from string
        checkboxValueList.value = props.prevAnswerText.split(" ／ ")
        selectedAnswer.value = checkboxValueList.value.join('、')
        // answer display option is not original text like "ア","イ","ウ"... | "Ⓐ","Ⓑ","Ⓒ"... | "1","2","3",...
        if(props.answer.display_options.choicesLabel !== 1){
          let originalAnswerText = props.answer.body.answers.map(i => i.answerText)
          // find original restore answer from choices label
          checkboxValueList.value = checkboxValueList.value.map(item => originalAnswerText[CHOICESLABEL[props.answer.display_options.choicesLabel].indexOf(item)])
        }
        emit('select-value', checkboxValueList.value)
      }
    }
  })

  function openPopup(answer) {
    // save previous focus element
    previousFocusElement.value = document.activeElement
    let client: any = refSelectBox.value[props.answerFormatId]
    let popup: any = refPopup.value[props.answerFormatId]
    let clientSize = client.getClientRects()
    let popSize = popup.getClientRects()
    isTopPart.value = false
    isBottomPart.value = false
    isLeftMostPart.value = false

    let agent = window.navigator.userAgent.toLowerCase();

    // 縦書きの場合（国語）
    if (props.isVertical) {
      // 選択式かつ幅が100％の場合はセレクトボックスと同じサイズを設定
      if(answer.answer_format === QUIZTYPE.SELECT && answer.display_options.choicesLayout === LAYOUT.FULL){
        popup.style.height = clientSize[0].height + "px"
        popup.style.top = clientSize[0].y + "px"
      }
      // 並び替えと選択式かつ幅が50％の場合、縦の長さを固定値に設定
      else{
        popup.style.height = popupFixedSize.value + "px"
        // セレクトボックスの位置のよってポップアップのtop位置を調整、これをしないと画面にはみ出される。
        if (clientSize[0].y < 200){
          isTopPart.value = true
          popup.style.top = clientSize[0].y + "px"
        }else if(clientSize[0].y > 400){
          isBottomPart.value = true
          popup.style.top = clientSize[0].y + clientSize[0].height - popupFixedSize.value + "px"
        }else{
          popup.style.top = (clientSize[0].y - (popupFixedSize.value / 2)) + (clientSize[0].height / 2) + "px"
        }
      }

      // ipadOSのipadだけ判定（生徒の対象のOSはchrome, window(edge), ipadのsafari)
      if(agent.indexOf('ipad') > -1 || agent.indexOf('macintosh') > -1 && 'ontouchend' in document)
      {
        if(ipadOsVersion(agent) != 14){
          popup.style.left = clientSize[0].x  + 45 + "px"
        }else{
          popup.style.left = clientSize[0].x  + "px"
        }
      }else{
        popup.style.left = clientSize[0].x  + 45 + "px"
      }
    }
    // 横書きの場合（国語以外の教科）
    else {
      // セレクトボックスサイズが固定幅より小さいの場合
      if (clientSize[0].width < popupFixedSize.value) {
        popup.style.width = popupFixedSize.value + "px"
        // 吹き出し▽の位置がセレクトボックスの真ん中に位置するように設定
        let left_pos = (clientSize[0].right - (popupFixedSize.value / 2)) - (clientSize[0].width / 2)
        if(left_pos < 0){
          if(document.documentElement.clientWidth < 1024){ //画面の幅サイズが1024pxより小さい場合▽の位置変更が必要
            isLeftMostPart.value = true
          }
          popup.style.left = 0 + "px" // leftがマイナスの場合表示が見切れてしまうので変更
        }else{
          popup.style.left = left_pos + "px"
        }
      }
      // セレクトボックスサイズが固定幅より大きい場合ポップアップサイズもセレクトボックスサイズと同じく設定
      else {
        popup.style.width = clientSize[0].width + "px"
        popup.style.left = clientSize[0].x + "px"
      }
      // セレクトボックスのTOP位置からポップアップの高さ分を抜く
      popup.style.top = clientSize[0].y - popSize[0].height + "px"
    }
    isAnswerPopup.value = true
    emit('set-tab-index', isAnswerPopup.value ? -1 : 0)
    if(isAnswerPopup.value) {
      nextTick(() => focusFirstElement())
    }
  }

  function closePopup(){
    isAnswerPopup.value = false
    emit('set-tab-index', 0)
  }

  // set selectedAnswer for show radio button value after selection
  function selectAnswerRadio(value: string){
    selectedAnswer.value = value
  }
  // save the selected radion button value to store and close select box
  function selectLabelRadio(value: string) {
    let arg = {format_id:"", value:""}
    arg.format_id = props.answerFormatId
    arg.value = value
    emit('select-value',arg)
    emit('set-tab-index', 0)
    emit('focus-next-item', previousFocusElement.value)
    isAnswerPopup.value = false
  }
  // set selectedAnswer for show multiple select button value after selection
  function selectAnswerCheckbox(value: Array<string|number>){
    selectedAnswer.value = value.join('、')
  }
  // save the selected value to store for multiple selection
  function selectLabelCheckbox(value: Array<string|number>){
    checkboxValueList.value = value
    emit('select-value', value)
  }

  // close multiple select box and focus next selectable element if press Ok button in select box
  function selectValue(){
    emit('set-tab-index', 0)
    emit('focus-next-item', null)
    isAnswerPopup.value = false
  }

  // ipadOSバージョン確認
  function ipadOsVersion(agent){
    let version = agent.match(/version\/(\d+).(\d+)/)
    if(version){
      return parseInt(version[1])
    }
  }
  function focusFirstElement() {
    const element: any = refPopup.value[props.answerFormatId]
    if(element) {
      const focusableElements = element.querySelectorAll('[tabindex]:not([tabindex="-1"])')
      focusableElements[0].focus()
    }
  }
</script>
